import { type PropsWithChildren, useState } from 'react';
import { neutral600 } from '../../design-tokens';
import {
  ErrorMessage,
  InputField,
  InputGroup,
  InputWrapper,
  Label,
} from './field.style';
import type { TextInputProps } from './field.type';

// Emulating the screen reader only from toolshed
// Browsers have issues displaying the placeholder + label on
// date inputs, so we need to hide the label visually
const ScreenReaderLabel = ({ children }: PropsWithChildren) => (
  <div className="sr-only">{children}</div>
);

export const Input = ({
  name,
  value,
  onChange,
  label,
  placeholder,
  showError,
  errorMessage,
  disabled,
  leftIcon: LeftIcon,
  leftIconProps = {},
  rightIcon: RightIcon,
  rightIconProps = {},
  className,
  type = 'text',
  onFocus,
  onBlur,
  ...rest
}: TextInputProps) => {
  const [focused, setFocused] = useState(false);

  const onFocusFn = () => {
    setFocused(true);
    onFocus && onFocus();
  };

  const onBlurFn = () => {
    setFocused(false);
    onBlur && onBlur();
  };

  const shouldShowError = showError || !!errorMessage;

  return (
    <InputWrapper className={className}>
      <InputGroup showError={shouldShowError} disabled={disabled}>
        {LeftIcon && (
          <LeftIcon size="small" color={neutral600} {...leftIconProps} />
        )}
        <InputField
          aria-label={label}
          value={value}
          onChange={onChange}
          name={name}
          id={name}
          filled={!!value || focused}
          hasLabel={!!label && type !== 'date'}
          onFocus={onFocusFn}
          onBlur={onBlurFn}
          placeholder={label ? undefined : placeholder}
          disabled={disabled}
          type={type}
          {...rest}
        />
        {label &&
          (type === 'date' ? (
            <ScreenReaderLabel>
              <label htmlFor={name}>{label}</label>
            </ScreenReaderLabel>
          ) : (
            <Label
              filled={!!value || focused}
              htmlFor={name}
              withIcon={!!LeftIcon}
            >
              {label}
            </Label>
          ))}
        {RightIcon && (
          <RightIcon
            size="small"
            color={neutral600}
            onClick={onFocusFn}
            {...rightIconProps}
          />
        )}
      </InputGroup>
      {shouldShowError && errorMessage && (
        <ErrorMessage>{errorMessage}</ErrorMessage>
      )}
    </InputWrapper>
  );
};
